import { useCallback, useMemo } from 'react';
import { generatePath, NavigateOptions, useNavigate } from 'react-router-dom';

import { ROUTE_PATH } from 'constants/routes';
import { DefaultPaymentTypeCode, RequisitesVerificationStatus } from 'enums';
import { Requisites } from 'types';

import { useMeta } from './use-meta';
import { useUserContextUtils } from './use-user-context-utils.hook';
import { useUser } from './use-user.hook';

export function usePayinRequisitesUtils() {
  const navigate = useNavigate();
  const { isTrader, isAdmin, isTechOperator } = useUser();
  const { payinRequisitesVerificationRequired } = useMeta();
  const { getPaymentTypeCode } = useUserContextUtils();

  const detailsUrl = useMemo(() => {
    if (isTrader) {
      return ROUTE_PATH.TRADER.PAYIN_REQUISITES_DETAILS;
    }
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.PAYIN_REQUISITES_DETAILS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.PAYIN_REQUISITES_DETAILS;
    }

    return '';
  }, [isTrader, isTechOperator, isAdmin]);

  const requisitesUrl = useMemo(() => {
    if (isTrader) {
      return ROUTE_PATH.TRADER.PAYIN_REQUISITES;
    }
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.PAYIN_REQUISITES;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.PAYIN_REQUISITES;
    }

    return '';
  }, [isTrader, isTechOperator, isAdmin]);

  const customerLimitsUrl = useMemo(() => {
    if (isTrader) {
      return ROUTE_PATH.TRADER.PAYIN_REQUISITES_CUSTOMER_LIMITS;
    }
    if (isAdmin) {
      return ROUTE_PATH.ADMIN.PAYIN_REQUISITES_CUSTOMER_LIMITS;
    }
    if (isTechOperator) {
      return ROUTE_PATH.TECH_OPERATOR.PAYIN_REQUISITES_CUSTOMER_LIMITS;
    }

    return '';
  }, [isTrader, isTechOperator, isAdmin]);

  const handleOpenPage = useCallback(
    (requisitesId: string, url: string, navigateOptions?: NavigateOptions) => {
      const path = generatePath(url, {
        id: requisitesId,
      });
      navigate(path, navigateOptions);
    },
    [navigate],
  );

  const isVerified = (requisites: Partial<Requisites>) => {
    if (!payinRequisitesVerificationRequired) {
      return true;
    }
    if (requisites.trader?.skipPayinRequisitesVerification) {
      return true;
    }

    return [
      RequisitesVerificationStatus.Success,
      RequisitesVerificationStatus.Disabled,
    ].includes((requisites as Requisites).verificationStatus);
  };

  const isVerificationDisabled = (requisites?: Partial<Requisites>) => {
    if (requisites?.trader?.skipPayinRequisitesVerification) {
      return true;
    }

    return (
      (requisites as Requisites)?.verificationStatus ===
      RequisitesVerificationStatus.Disabled
    );
  };

  const isNSPK = (requisites?: Partial<Requisites>) =>
    getPaymentTypeCode({ id: requisites?.paymentTypeId }) ===
    DefaultPaymentTypeCode.NSPK;

  return {
    requisitesUrl,
    detailsUrl,
    customerLimitsUrl,
    handleOpenPage,
    isVerified,
    isVerificationDisabled,
    isNSPK,
  };
}
